import { cn } from "@/core/utils";
import { useId, type ReactNode } from "react";
import { wobaText } from "./typography";

type DashboardMetricTileProps = {
  overline?: ReactNode;
  value: ReactNode;
  caption?: ReactNode;
  /**
   * `label`: valor em Label/Xlarge alinhado à esquerda (insights, Meus produtos).
   * `heading`: valor em Heading/Large centralizado (card Colaboradores).
   */
  variant?: "label" | "heading";
  className?: string;
  "data-testid"?: string;
};

/**
 * Métrica somente leitura no visual do Tile do Workbase DS. O `Tile` do DS
 * sempre renderiza um Avatar de ícone no topo (prop `icon` obrigatória) e o
 * Figma do Painel usa a variante sem ícone, por isso este componente local
 * replica o frame do Tile (padding 16, raio x6, stroke default) sem o Avatar.
 */
export function DashboardMetricTile({
  overline,
  value,
  caption,
  variant = "label",
  className,
  "data-testid": testId,
}: DashboardMetricTileProps) {
  const isHeading = variant === "heading";
  const overlineId = useId();

  return (
    <div
      role="group"
      aria-labelledby={overline ? overlineId : undefined}
      className={cn(
        "flex flex-col gap-4 p-4 min-w-0 border border-solid bg-[var(--woba-color-surface-default)] border-[color:var(--woba-color-stroke-default)] rounded-[var(--woba-radii-x6)]",
        isHeading && "items-center text-center",
        className,
      )}
      data-testid={testId}
    >
      {overline && (
        <span id={overlineId} style={wobaText.overlineMedium}>
          {overline}
        </span>
      )}
      {isHeading ? (
        <div className="flex flex-col gap-1 items-center">
          <span className="break-words" style={wobaText.headingLarge}>
            {value}
          </span>
          {caption && <span style={wobaText.captionMedium}>{caption}</span>}
        </div>
      ) : (
        <>
          <span className="break-words" style={wobaText.labelXlarge}>
            {value}
          </span>
          {caption && <span style={wobaText.captionMedium}>{caption}</span>}
        </>
      )}
    </div>
  );
}
